import { useCallback, useEffect, useState } from 'react';
import { Observable } from 'rxjs';

import { SortingProps } from '@block-explorer/ui';

import { FilterProps, PaginationProps } from '../components';
import {
  getBlockByHashAsync,
  getPaginatedBlocksAsync,
} from '../redux/paginated-blocks/actions';
import {
  getDeployByHashAsync,
  getPaginatedDeploysAsync,
} from '../redux/paginated-deploys/actions';
import { usePaginationState } from '../router/navigation';
import {
  addSignatureToMultisigTransactionRequestSubscription,
  adsContactRequestSubscription,
  BatchGetRequestParams,
  createActivityExportDeployRequestSubscription,
  createActivityExportTransactionRequestSubscription,
  getAccountActivityDeployStatusesSubscription,
  getAccountBalanceByPublicKeySubscription,
  getAccountContractPackagesSubscription,
  getAccountInfoSubscription,
  getAccountsPublicKeySubscription,
  getBidderDetailsDataSubscription,
  getBlockByHeightSubscription,
  getCentralizedAccountInfoSubscription,
  getContractEntryPointsSubscription,
  getContractEntryPointCostsSubscription,
  getContractInfoSubscription,
  getContractPackageCep18TokenListSubscription,
  getContractPackageContractsInfoSubscription,
  getContractPackageInfoSubscription,
  getCsprNameInfoRequestSubscription,
  getCurrencyRateSubscription,
  getDelegatorsTotalRewardsSubscription,
  getDeployRawDataSubscription,
  getEcosystemSustainIssuancesSubscription,
  getFriendlyMarketTokenPairBatchSubscription,
  getFtRatesHistorySubscription,
  getKeysToWhichAccountAssociatedSubscription,
  getMultisigTransactionRequestSubscription,
  getNetworkStatsSubscription,
  getPaginatedAccountCep18TokenListSubscription,
  getPaginatedAccountDelegationsSubscription,
  getPaginatedAccountDelegatorRewardsSubscription,
  getPaginatedAccountDelegatorRewardsByEraSubscription,
  getPaginatedAccountDeploysSubscription,
  getPaginatedAccountFeedTransactionsSubscription,
  getPaginatedAccountInfoSubscription,
  getPaginatedAccountNftCollectionsSubscription,
  getPaginatedAccountTokensByContractPackageHashSubscription,
  getPaginatedAccountTokensSubscription,
  getPaginatedAccountTokensTxsListSubscription,
  getPaginatedAccountTransfersSubscription,
  getPaginatedActivityExportRequestSubscription,
  getPaginatedBlockDeploysSubscription,
  getPaginatedContractPackageEntryPointsSubscription,
  getPaginatedContractPackageFeedTransactionsSubscription,
  getPaginatedContractPackageInvolvedTokensTxsListSubscription,
  getPaginatedContractPackageIssuedTokensTxsListSubscription,
  getPaginatedContractPackageNftOwnersSubscriptions,
  getPaginatedContractPackageOwnedNftsSubscriptions,
  getPaginatedContractPackagesSubscription,
  getPaginatedContractPackageTokensSubscription,
  getPaginatedContractsCep18TokenListSubscription,
  getPaginatedDailyDeployNumbersSubscription,
  getPaginatedDeployTransfersSubscription,
  getPaginatedDeploysSubscription,
  getPaginatedNftTokenActivitySubscription,
  getPaginatedPendingUndelegationsListSubscription,
  getPaginatedUrefDelegationsRequestSubscription,
  getPaginatedUrefRewardsRequestSubscription,
  getPaginatedValidatorAuctionDataSubscription,
  getPaginatedValidatorBlocksSubscription,
  getPaginatedValidatorEraDataSubscription,
  getPaginatedValidatorRewardsSubscription,
  getRatesHistorySubscription,
  getTotalEcosystemSustainIssuancesSubscription,
  getUrefDelegatorsTotalRewardsSubscription,
  getUrefTransfersRequestSubscription,
  getUrefUndelegationBalanceSubscription,
  getValidatorAuctionMetricsSubscription,
  getValidatorsDelegationsSubscription,
  getValidatorsTotalDelegatorRewardsSubscription,
  getValidatorsTotalRewardsSubscription,
  getValidatorsUnstakingBalanceSubscription,
  PaginatedResponse,
  PaginationParams,
  postAccountSubscription,
  postActivityExportRequestSubscription,
  PostRequestParams,
  RequestParams,
  submitActivityExportDeployRequestSubscription,
  submitActivityExportTransactionRequestSubscription,
  submitMultisigDeployRequestSubscription,
  submitMultisigTransactionRequestSubscription,
  useGetContractNftTokenSubscription,
} from '../services/event-store-service';

import {
  createGetByHashReducerHook,
  createPaginatedGetReducerHook,
} from '../redux/utils';
import { ErrorMessages } from '../domain/error-messages';
import { getAccountDelegationsAsync } from '../redux/account-delegations';
import { getActiveAccountDelegationsAsync } from '../redux/active-account-delegations';

export type ApiPaginatedGetResult<Entity> = {
  loading: boolean; // TODO: add support
  error: string | null; // TODO: add support
  data: Entity[] | null;
  paginationProps: PaginationProps;
  sortingProps: SortingProps;
  filterProps: FilterProps;
  reloadData: () => void;
};

const createPaginatedGetHook =
  <Entity extends any, Params extends RequestParams>(
    paginationStateKey: string,
    stream: (params: Params) => Observable<PaginatedResponse<Entity>>
  ) =>
  (
    requestParams:
      | (Params & Partial<PaginationParams> & { defaultPageSize?: number })
      | null
  ): ApiPaginatedGetResult<Entity> => {
    const [requestCounter, setRequestCounter] = useState(0);
    const [payload, setPayload] = useState<{
      data: PaginatedResponse<Entity> | null;
      loading: boolean;
      error: string | null;
    }>({
      data: null,
      loading: true,
      error: null,
    });

    const reloadData = useCallback(() => {
      setRequestCounter((state) => state + 1);
    }, []);

    const {
      currentPage: currentPageParam,
      perPage: perPageParam,
      setCurrentPage: setPaginationPage,
      setPerPage: setPaginationLimit,
      orderBy: orderByParam,
      orderDirection: orderDirectionParam,
      setOrder,
      filters,
      setFilters,
    } = usePaginationState(paginationStateKey, requestParams?.defaultPageSize);

    const { perPage, currentPage, orderBy, orderDirection } = {
      currentPage: currentPageParam,
      perPage: perPageParam,
      orderBy: orderByParam,
      orderDirection: orderDirectionParam,
      ...requestParams,
    };

    const total = payload.data?.item_count || 0;

    const paginationProps: PaginationProps = {
      itemCount: total,
      pageCount: payload.data?.page_count || 0,
      perPage: perPage,
      currentPage: currentPage,
      setPerPage: setPaginationLimit,
      setCurrentPage: (page: number) => {
        if (page > 0 && page <= total) {
          setPaginationPage(page);
        }
      },
      /* eslint-disable-next-line */
    };

    const sortingProps: SortingProps = {
      orderBy,
      orderDirection,
      setOrder,
    };

    const filterProps: FilterProps = {
      filters,
      setFilters,
    };

    useEffect(() => {
      if (requestParams == null) {
        setPayload({
          data: null,
          loading: false,
          error: null,
        });
        return;
      }

      const nonNullableFilters = Object.fromEntries(
        Object.entries(filterProps?.filters || {}).filter(
          ([_, value]) => value != null && value !== ''
        )
      );

      const subscription = stream({
        page: currentPageParam,
        page_size: perPageParam,
        order_by: orderByParam,
        order_direction: orderDirectionParam,
        ...nonNullableFilters,
        ...requestParams,
      }).subscribe({
        next: (res) => {
          setPayload({
            data: res,
            loading: false,
            error: null,
          });
        },
        error: (err) => {
          setPayload({
            data: null,
            loading: false,
            error: err,
          });
          console.error(err);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
      /* eslint-disable-next-line */
    }, [
      currentPageParam,
      perPageParam,
      orderByParam,
      orderDirectionParam,
      JSON.stringify(requestParams),
      requestCounter,
      filterProps.filters,
    ]);

    return {
      loading: payload.loading,
      error: payload.error,
      data: payload.data?.data ?? null,
      paginationProps: {
        ...paginationProps,
        perPage: requestParams?.page_size ?? paginationProps.perPage,
      },
      sortingProps,
      filterProps,
      reloadData,
    };
  };

export type ApiGetResult<Entity extends any> = {
  loading: boolean;
  error: string | null;
  data: Entity | null;
  reloadData: () => void;
};

const createGetHook =
  <Entity extends any, Params extends RequestParams | BatchGetRequestParams>(
    stream: (params: Params) => Observable<Entity>
  ) =>
  (requestParams: Params | null): ApiGetResult<Entity> => {
    const [requestCounter, setRequestCounter] = useState(0);
    const [payload, setPayload] = useState<{
      data: Entity | null;
      loading: boolean;
      error: string | null;
    }>({
      data: null,
      loading: true,
      error: null,
    });

    const reloadData = useCallback(() => {
      setRequestCounter((state) => state + 1);
    }, []);

    useEffect(() => {
      if (requestParams == null) {
        setPayload({
          data: null,
          loading: false,
          error: null,
        });
        return;
      }

      const subscription = stream(requestParams).subscribe({
        next: (res) => {
          if (res && res['error']) {
            setPayload({
              data: null,
              loading: false,
              error: res['error'].message,
            });
          } else {
            setPayload({
              data: res,
              loading: false,
              error: null,
            });
          }
        },
        error: (err) => {
          setPayload({
            data: null,
            loading: false,
            error: ErrorMessages.DefaulRpcErrorMessage,
          });
          console.error(err);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
      /* eslint-disable-next-line */
    }, [requestCounter, JSON.stringify(requestParams)]);

    return {
      ...payload,
      reloadData,
    };
  };

export type ApiPostResult<
  Entity extends any,
  Params extends PostRequestParams,
  PostQueryRequestParams = undefined,
> = {
  loading: boolean;
  error: string | null;
  data: Entity | null;
  postData: (
    params: Params,
    queryRequestParams: PostQueryRequestParams
  ) => Promise<Entity | null>;
};

export const createPostHook =
  <
    Entity extends any,
    Params extends PostRequestParams,
    PostQueryRequestParams = undefined,
  >(
    stream: (
      params: Params,
      queryRequestParams?: PostQueryRequestParams
    ) => Observable<Entity>
  ) =>
  (): ApiPostResult<Entity, Params, PostQueryRequestParams> => {
    const [payload, setPayload] = useState<{
      data: Entity | null;
      loading: boolean;
      error: string | null;
    }>({
      data: null,
      loading: true,
      error: null,
    });

    const promise = new Promise<Entity | null>((resolve, reject) => {
      if (payload.error) {
        reject(payload.error);
      } else {
        resolve(payload.data);
      }
    });

    const postData = useCallback(
      (
        requestParams: Params,
        queryRequestParams?: PostQueryRequestParams
      ): Promise<any> => {
        const subscription = stream(
          requestParams,
          queryRequestParams
        ).subscribe({
          next: (res) => {
            setPayload({
              data: res,
              loading: false,
              error: null,
            });
          },
          error: (err) => {
            setPayload({
              data: null,
              loading: false,
              error: err,
            });
            console.error(err);
          },
        });

        return promise;
      },
      [stream]
    );

    return {
      ...payload,
      postData,
    };
  };

/**
 * BLOCK */
export const usePaginatedGetBlocks = createPaginatedGetReducerHook(
  'getPaginatedBlocksSubscription',
  'paginatedBlocks',
  getPaginatedBlocksAsync
);

export const useGetBlock = createGetByHashReducerHook(
  'paginatedBlocks',
  getBlockByHashAsync
);

export const useGetPaginatedBlockDeploys = createPaginatedGetHook(
  'getPaginatedBlockDeploysSubscription',
  getPaginatedBlockDeploysSubscription
);

export const useGetBlockByHeight = createGetHook(getBlockByHeightSubscription);

export const useCurrencyRateAmount = createGetHook(getCurrencyRateSubscription);

/**
 * DEPLOY */
export const useGetDeploys = createPaginatedGetHook(
  'getPaginatedDeploysSubscription',
  getPaginatedDeploysSubscription
);

export const useGetPaginatedDeploys = createPaginatedGetReducerHook(
  'useGetPaginatedDeploys',
  'paginatedDeploys',
  getPaginatedDeploysAsync
);

/**
 * @todo: looks like it's not used anymore
 */
export const useGetPaginatedDeployTransfers = createPaginatedGetHook(
  'getPaginatedDeployTransfersSubscription',
  getPaginatedDeployTransfersSubscription
);

export const useGetDeploy = createGetByHashReducerHook(
  'paginatedDeploys',
  getDeployByHashAsync
);

export const useGetPaginatedDailyDeployNumbers = createGetHook(
  getPaginatedDailyDeployNumbersSubscription
);

export const useGetDeployRawData = createGetHook(getDeployRawDataSubscription);

/**
 * CONTRACT */
export const useGetContractPackageInfo = createGetHook(
  getContractPackageInfoSubscription
);

export const useGetContractPackageContractsInfo = createPaginatedGetHook(
  'getContractPackageContractsInfoSubscription',
  getContractPackageContractsInfoSubscription
);

export const useGetContractInfo = createGetHook(getContractInfoSubscription);
export const useGetContractEntryPoints = createPaginatedGetHook(
  'getContractEntryPointsSubscription',
  getContractEntryPointsSubscription
);
export const useGetContractEntryPointCosts = createGetHook(
  getContractEntryPointCostsSubscription
);
export const useGetPaginatedContractPackageEntryPoints = createPaginatedGetHook(
  'getPaginatedContractPackageEntryPointsSubscription',
  getPaginatedContractPackageEntryPointsSubscription
);

export const useGetContractPackages = createPaginatedGetHook(
  'getPaginatedContractPackages',
  getPaginatedContractPackagesSubscription
);

export const useGetPaginatedContractPackageFeedTransactions =
  createPaginatedGetHook(
    'getPaginatedContractPackageFeedTransactionsSubscription',
    getPaginatedContractPackageFeedTransactionsSubscription
  );

export const useGetPaginatedContractPackageTokens = createPaginatedGetHook(
  'getPaginatedContractPackageTokens',
  getPaginatedContractPackageTokensSubscription
);

export const useGetContractNftToken = createGetHook(
  useGetContractNftTokenSubscription
);

export const useGetPaginatedNftTokenActivity = createPaginatedGetHook(
  'getPaginatedNftTokenActivitySubscription',
  getPaginatedNftTokenActivitySubscription
);

export const useGetPaginatedContractPackageNftOwners = createPaginatedGetHook(
  'getPaginatedContractPackageNftOwners',
  getPaginatedContractPackageNftOwnersSubscriptions
);

export const useGetPaginatedContractPackageOwnedNfts = createPaginatedGetHook(
  'getPaginatedContractPackageOwnedNftsSubscriptions',
  getPaginatedContractPackageOwnedNftsSubscriptions
);

/**
 * ACCOUNT */
export const useGetPaginatedAccountDeploys = createPaginatedGetHook(
  'getPaginatedAccountDeploysSubscription',
  getPaginatedAccountDeploysSubscription
);

export const useGetPaginatedAccountFeedTransactions = createPaginatedGetHook(
  'getPaginatedAccountFeedTransactionsSubscription',
  getPaginatedAccountFeedTransactionsSubscription
);

export const useGetPaginatedAccountTransfers = createPaginatedGetHook(
  'getPaginatedAccountTransfersSubscription',
  getPaginatedAccountTransfersSubscription
);

export const useGetActiveAccountDelegations = createPaginatedGetReducerHook(
  'getActiveAccountDelegationsSubscription',
  'activeAccountDelegations',
  getActiveAccountDelegationsAsync
);

export const useGetPaginatedAccountDelegations = createPaginatedGetReducerHook(
  'getPaginatedAccountDelegationsSubscription',
  'accountDelegations',
  getAccountDelegationsAsync
);

export const useGetPaginatedAccountTokens = createPaginatedGetHook(
  'getPaginatedAccountTokensSubscription',
  getPaginatedAccountTokensSubscription
);

export const useGetPaginatedAccountNftCollections = createPaginatedGetHook(
  'getPaginatedAccountNftCollectionsSubscription',
  getPaginatedAccountNftCollectionsSubscription
);

export const useGetPaginatedAccountDelegatorRewards = createPaginatedGetHook(
  'getPaginatedAccountDelegatorRewardsSubscription',
  getPaginatedAccountDelegatorRewardsSubscription
);

export const useGetPaginatedAccountDelegatorRewardsByEra =
  createPaginatedGetHook(
    'getPaginatedAccountDelegatorRewardsByEraSubscription',
    getPaginatedAccountDelegatorRewardsByEraSubscription
  );

export const useGetAccountsPublicKey = createPaginatedGetHook(
  'getAccountsPublicKeySubscription',
  getAccountsPublicKeySubscription
);

export const useGetPaginatedAccountInfo = createPaginatedGetHook(
  'getPaginatedAccountInfoSubscription',
  getPaginatedAccountInfoSubscription
);

export const useGetCentralizedAccountInfo = createGetHook(
  getCentralizedAccountInfoSubscription
);

export const useGetAccountInfo = createGetHook(getAccountInfoSubscription);

export const usePostAccountsWithInfo = createPaginatedGetHook(
  'postAccountSubscription',
  postAccountSubscription
);

export const usePostAccounts = createPostHook(postAccountSubscription);

export const useGetAccountBalanceByPublicKey = createGetHook(
  getAccountBalanceByPublicKeySubscription
);

export const useGetAccountContractPackages = createGetHook(
  getAccountContractPackagesSubscription
);

/**
 * CEP-18 **/
export const useGetAccountCep18TokenList = createPaginatedGetHook(
  'getAccountCep18TokenListSubscription',
  getPaginatedAccountCep18TokenListSubscription
);

export const useGetPaginatedContractsCep18TokenList = createPaginatedGetHook(
  'getPaginatedContractsCep18TokenList',
  getPaginatedContractsCep18TokenListSubscription
);

export const useGetContractPackageCep18TokenList = createPaginatedGetHook(
  'getContractPackageCep18TokenList',
  getContractPackageCep18TokenListSubscription
);

export const useGetPaginatedAccountCep18TxsList = createPaginatedGetHook(
  'getPaginatedAccountTokensTxsListSubscription',
  getPaginatedAccountTokensTxsListSubscription
);

export const useGetPaginatedContractPackageIssuedTokensTxsList =
  createPaginatedGetHook(
    'getPaginatedContractPackageIssuedTokensTxsListSubscription',
    getPaginatedContractPackageIssuedTokensTxsListSubscription
  );

export const useGetPaginatedContractPackageInvolvedTokensTxsList =
  createPaginatedGetHook(
    'getPaginatedContractPackageInvolvedTokensTxsListSubscription',
    getPaginatedContractPackageInvolvedTokensTxsListSubscription
  );

export const useGetPaginatedPendingUndelegationsList = createPaginatedGetHook(
  'getPaginatedPendingUndelegationsListSubscription',
  getPaginatedPendingUndelegationsListSubscription
);

/**
 * VALIDATOR **/
export const useGetValidatorAuctionMetrics = createGetHook(
  getValidatorAuctionMetricsSubscription
);

export const useGetValidatorsEraData = createPaginatedGetHook(
  'getPaginatedValidatorEraDataSubscription',
  getPaginatedValidatorEraDataSubscription
);

export const useGetValidatorsAuctionData = createPaginatedGetHook(
  'getPaginatedValidatorAuctionDataSubscription',
  getPaginatedValidatorAuctionDataSubscription
);

export const useGetPaginatedValidatorBlocks = createPaginatedGetHook(
  'getPaginatedValidatorBlocksSubscription',
  getPaginatedValidatorBlocksSubscription
);

export const useGetPaginatedValidatorRewards = createPaginatedGetHook(
  'getPaginatedValidatorRewardsSubscription',
  getPaginatedValidatorRewardsSubscription
);

export const useGetValidatorTotalRewards = createGetHook(
  getValidatorsTotalRewardsSubscription
);

export const useGetValidatorDelegatorsTotalRewards = createGetHook(
  getValidatorsTotalDelegatorRewardsSubscription
);

export const useGetValidatorsDelegations = createPaginatedGetHook(
  'getValidatorsDelegationsSubscription',
  getValidatorsDelegationsSubscription
);

export const useGetValidatorsUnstakingBalance = createGetHook(
  getValidatorsUnstakingBalanceSubscription
);

/**
 * BIDDER **/
export const useGetBidderDetailsData = createGetHook(
  getBidderDetailsDataSubscription
);

/**
 * DELEGATOR **/
export const useGetDelegatorTotalRewards = createGetHook(
  getDelegatorsTotalRewardsSubscription
);

/**
 * UNISWAP **/
export const useGetFriendlyMarketTokenPairs = createGetHook(
  getFriendlyMarketTokenPairBatchSubscription
);

/**
 * CURRENCY **/

/**
 * ACTIVITY EXPORT */
export const useGetPaginatedAccountTokensByContractPackageHash =
  createPaginatedGetHook(
    'getAccountTokensByContractPackageHashSubscription',
    getPaginatedAccountTokensByContractPackageHashSubscription
  );

export const useGetActivityExportRequest = createPaginatedGetHook(
  'getPaginatedPaginatedActivityExportSubscription',
  getPaginatedActivityExportRequestSubscription
);

export const usePostActivityExportRequest = createPostHook(
  postActivityExportRequestSubscription
);

export const useCreateActivityExportDeployRequest = ({
  networkMajorVersion,
}) => {
  const subscription =
    networkMajorVersion === '2'
      ? createActivityExportTransactionRequestSubscription
      : createActivityExportDeployRequestSubscription;

  return createPostHook(subscription)();
};

export const useSubmitActivityExportDeployRequest = ({
  networkMajorVersion,
}) => {
  const subscription =
    networkMajorVersion === '2'
      ? submitActivityExportTransactionRequestSubscription
      : submitActivityExportDeployRequestSubscription;

  return createPostHook(subscription)();
};

export const useGetAccountActivityDeployStatuses = createGetHook(
  getAccountActivityDeployStatusesSubscription
);

/**
 * ANALYTICS */
export const useGetNetworkStats = createGetHook(getNetworkStatsSubscription);
export const useGetRatesHistory = createGetHook(getRatesHistorySubscription);
export const useGetFtRatesHistory = createPaginatedGetHook(
  'getFtRatesHistorySubscription',
  getFtRatesHistorySubscription
);

/**
 * MULTISIG */
export const useSubmitMultisigDeployRequest = ({ networkMajorVersion }) => {
  const subscription =
    networkMajorVersion === '2'
      ? submitMultisigTransactionRequestSubscription
      : submitMultisigDeployRequestSubscription;

  return createPostHook(subscription)();
};

export const useAddSignatureToMultisigDeployRequest = createPostHook(
  addSignatureToMultisigTransactionRequestSubscription
);

export const useGetMultisigDeployRequest = createGetHook(
  getMultisigTransactionRequestSubscription
);

export const useGetKeysToWhichAccountAssociated = createPaginatedGetHook(
  'getKeysToWhichAccountAssociatedSubscription',
  getKeysToWhichAccountAssociatedSubscription
);

/**
 * ADS */
export const useAdsContactRequest = createPostHook(
  adsContactRequestSubscription
);

/**
 * UREF */
export const useGetPaginatedUrefTransfers = createPaginatedGetHook(
  'getPaginatedUrefTransfersSubscription',
  getUrefTransfersRequestSubscription
);

export const useGetPaginatedUrefDelegations = createPaginatedGetHook(
  'getPaginatedUrefDelegationsRequestSubscription',
  getPaginatedUrefDelegationsRequestSubscription
);

export const useGetPaginatedUrefRewards = createPaginatedGetHook(
  'getPaginatedUrefRewardsRequestSubscription',
  getPaginatedUrefRewardsRequestSubscription
);

export const useGetUrefDelegatorsTotalRewards = createGetHook(
  getUrefDelegatorsTotalRewardsSubscription
);

export const useGetUrefUndelegationBalance = createGetHook(
  getUrefUndelegationBalanceSubscription
);

/**
 * CSPR NAME */
export const useGetCsprNameInfo = createGetHook(
  getCsprNameInfoRequestSubscription
);

/**
 * INFLATION  */
export const useGetTotalEcosystemSustainIssuances = createGetHook(
  getTotalEcosystemSustainIssuancesSubscription
);

export const useGetEcosystemSustainIssuances = createPaginatedGetHook(
  'getEcosystemSustainIssuancesSubscription',
  getEcosystemSustainIssuancesSubscription
);
